import { useEffect, useState } from "react";
import { List, ShowButton, useTable } from "@refinedev/antd";
import { Table, Tag, Input, Space, Button, Modal, Select, message } from "antd";
import { TagsOutlined, MessageOutlined } from "@ant-design/icons";
import { Link } from "react-router";
import type { UserListItem, OnboardingDevice, OnboardingOS } from "../../types";
import { DEVICE_LABEL, OS_LABEL } from "../../types";
import { fmtRub, fmtDateTime } from "../../utils";
import { API_URL } from "../../dataProvider";

interface TagRow { id: number; name: string; color: string; users_count: number; }

export const UserList = () => {
  const { tableProps, setFilters, filters } =
    useTable<UserListItem>({
      syncWithLocation: true,
      pagination: { mode: "server", pageSize: 50 },
    });

  const q = filters.find(f => "field" in f && f.field === "q");

  const [selected, setSelected] = useState<number[]>([]);
  const [tagModal, setTagModal] = useState(false);
  const [tags, setTags] = useState<TagRow[]>([]);
  const [chosenTags, setChosenTags] = useState<number[]>([]);

  useEffect(() => {
    if (tagModal) {
      fetch(`${API_URL}/admin/tags`, { credentials: "include" })
        .then(r => r.ok ? r.json() : []).then(setTags);
    }
  }, [tagModal]);

  const assignBulk = async () => {
    if (selected.length === 0 || chosenTags.length === 0) {
      message.warning("Выберите юзеров и метки"); return;
    }
    const r = await fetch(`${API_URL}/admin/users/tags/assign-bulk`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_ids: selected, tag_ids: chosenTags }),
    });
    if (r.ok) {
      message.success(`Назначено ${chosenTags.length} меток ${selected.length} юзерам`);
      setTagModal(false); setChosenTags([]); setSelected([]);
    } else {
      message.error((await r.text()).slice(0, 200));
    }
  };

  return (
    <List title="Юзеры" breadcrumb={false}>
      <Space style={{ marginBottom: 12, justifyContent: "space-between", width: "100%" }} wrap>
        <Input.Search
          placeholder="Поиск: tg_id, username или имя"
          allowClear
          defaultValue={(q && "value" in q ? q.value : "") as string}
          onSearch={(v) => setFilters([{ field: "q", operator: "eq", value: v ?? "" }], "replace")}
          style={{ width: 320 }}
        />
        {selected.length > 0 && (
          <Space>
            <span>Выбрано: <strong>{selected.length}</strong></span>
            <Button icon={<TagsOutlined />} onClick={() => setTagModal(true)}>
              Назначить метку
            </Button>
            <Button size="small" type="text" onClick={() => setSelected([])}>
              Очистить
            </Button>
          </Space>
        )}
      </Space>
      <Modal title={`Назначить метки (${selected.length} юзеров)`}
             open={tagModal} onCancel={() => setTagModal(false)}
             onOk={assignBulk} okText="Применить">
        <Select mode="multiple" style={{ width: "100%" }}
          placeholder="Выберите одну или несколько меток"
          value={chosenTags} onChange={setChosenTags}
          options={tags.map(t => ({
            value: t.id,
            label: <Tag color={t.color}>{t.name}</Tag>,
          }))}
        />
      </Modal>
      <Table {...tableProps} rowKey="tg_id" scroll={{ x: 900 }}
        rowSelection={{
          selectedRowKeys: selected,
          onChange: (keys) => setSelected(keys as number[]),
          preserveSelectedRowKeys: true,
        }}>
        <Table.Column<UserListItem>
          dataIndex="tg_id" title="Telegram ID" sorter
          render={(v: number) => <Link to={`/users/${v}`}>{v}</Link>}
        />
        <Table.Column dataIndex="username" title="@username"
          render={(v: string | null) => v ? `@${v}` : <span style={{ opacity: 0.4 }}>—</span>} />
        <Table.Column<UserListItem>
          title="Имя"
          render={(_, r) => [r.first_name, r.last_name].filter(Boolean).join(" ") || "—"}
        />
        <Table.Column dataIndex="active_subs_cnt" title="Активные UUID"
          render={(v: number) => v > 0 ? <Tag color="green">{v}</Tag> : "—"} />
        <Table.Column dataIndex="payments_total_rub" title="Сумма платежей"
          sorter render={(v: number) => fmtRub(v)} />
        <Table.Column<UserListItem>
          title="Устройство"
          render={(_, r) => {
            // Анкета не пройдена — серая плашка с прогрессом 0..3 шага.
            if (r.onboarding_step < 4) {
              return r.onboarding_step === 0
                ? <Tag>не начата</Tag>
                : <Tag color="orange">шаг {r.onboarding_step}/3</Tag>;
            }
            const d = r.device_type && DEVICE_LABEL[r.device_type as OnboardingDevice];
            const o = r.device_os && OS_LABEL[r.device_os as OnboardingOS];
            return (
              <Space size={2} wrap>
                {d && <Tag color="blue">{d}</Tag>}
                {o && <Tag>{o}</Tag>}
              </Space>
            );
          }}
        />
        <Table.Column<UserListItem>
          title="Флаги"
          render={(_, r) => (
            <Space size={4}>
              {r.flagged_abuse && <Tag color="red">⚠ flagged</Tag>}
              {r.bot_blocked && <Tag>🚫 заблокировал бот</Tag>}
            </Space>
          )}
        />
        <Table.Column dataIndex="created_at" title="Регистрация"
          sorter render={fmtDateTime} />
        <Table.Column dataIndex="last_seen_at" title="Был онлайн"
          render={fmtDateTime} />
        <Table.Column<UserListItem>
          title="" fixed="right" width={120}
          render={(_, r) => (
            <Space size={4}>
              <Link to={`/dialogs?user=${r.tg_id}`}>
                <Button
                  size="small"
                  type="primary"
                  ghost
                  icon={<MessageOutlined />}
                  title="Написать в Telegram"
                />
              </Link>
              <ShowButton hideText size="small" recordItemId={r.tg_id} />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
